import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { useState } from "react";

const quiz = {
  title: "Which Tesla Are You?",
  description: "Discover your perfect Tesla match with this fun personality quiz.",
  questions: [
    {
      q: "Your driving style?",
      options: ["Performance-focused", "Eco-conscious", "Practical", "Adventurous"],
    },
  ],
};

const polls = [
  {
    title: "What SpaceX achievement excites you most?",
    options: [
      { text: "Mars colonization plans", votes: 2840 },
      { text: "Reusable rockets", votes: 1920 },
      { text: "Starlink global coverage", votes: 3120 },
      { text: "Space tourism", votes: 2450 },
    ],
  },
  {
    title: "Would you invest in Neuralink?",
    options: [
      { text: "Absolutely, cutting-edge tech", votes: 3500 },
      { text: "Maybe, with more data", votes: 2100 },
      { text: "Too risky for me", votes: 1200 },
      { text: "Undecided", votes: 890 },
    ],
  },
];

export default function EngagementTools() {
  const [selectedQuiz, setSelectedQuiz] = useState(false);
  const [selectedPolls, setSelectedPolls] = useState<{ [key: number]: number }>({});

  const getTotalVotes = (poll: any) =>
    poll.options.reduce((sum: number, opt: any) => sum + opt.votes, 0);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] },
    },
  };

  const progressVariants = {
    hidden: { scaleX: 0 },
    visible: (i: number) => ({
      scaleX: 1,
      transition: {
        delay: i * 0.1 + 0.3,
        duration: 0.8,
        ease: [0.4, 0, 0.2, 1],
      },
    }),
  };

  return (
    <motion.div
      className="space-y-16"
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
    >
      {/* Quiz Card */}
      <motion.div variants={itemVariants} whileHover={{ y: -5 }}>
        <div className="border border-border p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-8 hover:border-foreground transition-colors cursor-pointer">
          <div className="flex-1">
            <motion.h3
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="text-2xl font-medium text-foreground mb-3"
            >
              {quiz.title}
            </motion.h3>
            <motion.p
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="text-muted-foreground mb-6"
            >
              {quiz.description}
            </motion.p>
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              <Button className="bg-foreground text-background hover:bg-foreground/90 font-medium">
                Take the Quiz
              </Button>
            </motion.div>
          </div>
          <motion.div
            className="text-5xl opacity-20 flex-shrink-0"
            animate={{ y: [0, 15, 0] }}
            transition={{ duration: 4, repeat: Infinity }}
          >
            🚗
          </motion.div>
        </div>
      </motion.div>

      {/* Polls Section */}
      <motion.div variants={itemVariants}>
        <h3 className="text-lg font-medium text-foreground uppercase tracking-widest mb-8">
          Community Polls
        </h3>
        <motion.div
          className="space-y-6"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
        >
          {polls.map((poll, pollIdx) => {
            const total = getTotalVotes(poll);
            return (
              <motion.div
                key={pollIdx}
                variants={itemVariants}
              >
                <div className="border border-border p-8 hover:border-foreground transition-colors">
                  <motion.h4
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: pollIdx * 0.05 + 0.2, duration: 0.6 }}
                    className="text-base font-medium text-foreground mb-8"
                  >
                    {poll.title}
                  </motion.h4>
                  <motion.div
                    className="space-y-6"
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-30px" }}
                  >
                    {poll.options.map((option, optIdx) => {
                      const percentage = Math.round(
                        (option.votes / total) * 100
                      );
                      return (
                        <motion.div
                          key={optIdx}
                          variants={itemVariants}
                        >
                          <div className="mb-2 flex items-center justify-between">
                            <label className="text-sm font-medium text-foreground cursor-pointer">
                              {option.text}
                            </label>
                            <motion.span
                              initial={{ opacity: 0 }}
                              whileInView={{ opacity: 1 }}
                              transition={{
                                delay: pollIdx * 0.1 + optIdx * 0.05 + 0.3,
                                duration: 0.6,
                              }}
                              className="text-xs text-muted-foreground"
                            >
                              {percentage}% ({option.votes.toLocaleString()})
                            </motion.span>
                          </div>
                          <div className="w-full bg-border h-1.5 overflow-hidden">
                            <motion.div
                              className="h-full bg-foreground origin-left"
                              custom={optIdx}
                              variants={progressVariants}
                              initial="hidden"
                              whileInView="visible"
                              viewport={{ once: true, margin: "-50px" }}
                              style={{
                                width: `${percentage}%`,
                              }}
                            />
                          </div>
                        </motion.div>
                      );
                    })}
                  </motion.div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </motion.div>

      {/* CTA */}
      <motion.div
        variants={itemVariants}
        className="text-center py-12"
      >
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="text-muted-foreground mb-6"
        >
          More quizzes, polls, and interactive experiences await verified members.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          <Button className="bg-foreground text-background hover:bg-foreground/90 font-medium">
            Join the Community
          </Button>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}